import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/**
 * Kapsam dışı müşteriye süreli erişim talebi. Onaylı ve süresi dolmamış talep müşteriyi listede ve detayda
 * görünür kılar; süre bitince erişim kendiliğinden kapanır. Karar crm:access:approve yetkisindedir.
 */
export const CRM_ACCESS_APPROVE_PERM = 'crm:access:approve'

export type AccessStatus = 'pending' | 'approved' | 'rejected' | 'revoked' | 'cancelled'
export type AccessFilter = 'pending' | 'active' | 'expired' | 'rejected' | 'revoked' | 'all'

/** Talep edilebilen süreler (dakika): 1 saat, 8 saat, 1 gün, 3 gün, 7 gün. */
export const ACCESS_DURATIONS = [60, 480, 1440, 4320, 10080] as const

export type AccessRequest = {
  id: string
  accountId: string
  accountTitle: string
  requestedBy: string
  requestedByName: string
  reason: string
  durationMinutes: number
  status: AccessStatus
  active: boolean
  decidedByName: string
  decidedAt: string | null
  decisionNote: string
  startsAt: string | null
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string
}

export type AccessLookupItem = { accountId: string; title: string; pendingRequestId: string }
export type AccessActivity = { at: string; method: string; path: string; action: string; status: number }

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const opt = (v: unknown) => (v ? String(v) : null)

function mapRequest(r: Row): AccessRequest {
  return {
    id: s(r.id),
    accountId: s(r.account_id),
    accountTitle: s(r.account_title),
    requestedBy: s(r.requested_by),
    requestedByName: s(r.requested_by_name),
    reason: s(r.reason),
    durationMinutes: Number(r.duration_minutes) || 0,
    status: (s(r.status) || 'pending') as AccessStatus,
    active: r.active === true,
    decidedByName: s(r.decided_by_name),
    decidedAt: opt(r.decided_at),
    decisionNote: s(r.decision_note),
    startsAt: opt(r.starts_at),
    expiresAt: opt(r.expires_at),
    revokedAt: opt(r.revoked_at),
    createdAt: s(r.created_at),
  }
}

export const accessKeys = {
  all: ['crm', 'access'] as const,
  list: (filter: AccessFilter, mine: boolean) => ['crm', 'access', 'list', filter, mine] as const,
  lookup: (q: string) => ['crm', 'access', 'lookup', q] as const,
  activity: (id: string) => ['crm', 'access', 'activity', id] as const,
}

export const useAccessRequests = (filter: AccessFilter, mine: boolean) =>
  useQuery({
    queryKey: accessKeys.list(filter, mine),
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/crm/access/requests', { status: filter === 'all' ? undefined : filter, mine: mine ? '1' : undefined })).map(mapRequest),
  })

export const useAccessLookup = (q: string) =>
  useQuery({
    queryKey: accessKeys.lookup(q),
    enabled: q.trim().length >= 3,
    staleTime: 30_000,
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/crm/access/lookup', { q })).map((r) => ({
        accountId: s(r.account_id),
        title: s(r.title),
        pendingRequestId: s(r.pending_request_id),
      })) as AccessLookupItem[],
  })

export const useAccessActivity = (id: string | null) =>
  useQuery({
    queryKey: accessKeys.activity(id ?? ''),
    enabled: !!id,
    queryFn: async () =>
      asList(await api.get<Row[]>(`/api/v1/crm/access/requests/${id}/activity`)).map((r) => ({
        at: s(r.at),
        method: s(r.method),
        path: s(r.path),
        action: s(r.action),
        status: Number(r.status) || 0,
      })) as AccessActivity[],
  })

/** Karar ve iptaller müşteri görünürlüğünü değiştirir: tüm CRM sorguları tazelenir. */
function useAccessMut<A>(fn: (arg: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['crm'] }) })
}

export const useCreateAccessRequest = () =>
  useAccessMut((input: { accountId: string; reason: string; durationMinutes: number }) =>
    api.post('/api/v1/crm/access/requests', { account_id: input.accountId, reason: input.reason.trim(), duration_minutes: input.durationMinutes }),
  )

export const useDecideAccess = () =>
  useAccessMut(({ id, action, note, durationMinutes }: { id: string; action: 'approve' | 'reject' | 'revoke'; note: string; durationMinutes?: number }) =>
    api.post(`/api/v1/crm/access/requests/${id}/${action}`, { note: note.trim(), duration_minutes: durationMinutes ?? 0 }),
  )

export const useCancelAccess = () => useAccessMut((id: string) => api.post(`/api/v1/crm/access/requests/${id}/cancel`, {}))
